import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { settlementsApi, type CreateSettlementInput } from "./api";
import { saleKeys } from "../sales/hooks";

export const settlementKeys = {
  all: ["settlements"] as const,
  pending: (params: Parameters<typeof settlementsApi.pending>[0]) => [...settlementKeys.all, "pending", params] as const,
  lists: () => [...settlementKeys.all, "list"] as const,
  list: (params: Parameters<typeof settlementsApi.list>[0]) => [...settlementKeys.lists(), params] as const,
  details: () => [...settlementKeys.all, "detail"] as const,
  detail: (id: number) => [...settlementKeys.details(), id] as const,
};

export function useSettlementsPending(params: Parameters<typeof settlementsApi.pending>[0] = {}, enabled = true) {
  return useQuery({
    queryKey: settlementKeys.pending(params),
    queryFn: () => settlementsApi.pending(params),
    enabled,
  });
}

export function useSettlements(params: Parameters<typeof settlementsApi.list>[0] = {}) {
  return useQuery({
    queryKey: settlementKeys.list(params),
    queryFn: () => settlementsApi.list(params),
  });
}

export function useSettlement(id: number | null) {
  return useQuery({
    queryKey: settlementKeys.detail(id ?? -1),
    queryFn: () => settlementsApi.get(id!),
    enabled: id != null,
  });
}

// Confirmar una liquidación mueve plata "real": invalida no solo sus propias
// queries sino también ventas (cada pago liquidado deja de aparecer como
// pendiente) y el resumen del día (pasa a sumar en settlementsReceivedCents).
export function useCreateSettlement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSettlementInput) => settlementsApi.create(body),
    onSuccess: (settlement) => {
      qc.setQueryData(settlementKeys.detail(settlement.id), settlement);
      qc.invalidateQueries({ queryKey: settlementKeys.all });
      qc.invalidateQueries({ queryKey: saleKeys.lists() });
      qc.invalidateQueries({ queryKey: saleKeys.details() });
      qc.invalidateQueries({ queryKey: ["summary"] });
    },
  });
}

export function useRevertSettlement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => settlementsApi.revert(id),
    onSuccess: (settlement) => {
      qc.setQueryData(settlementKeys.detail(settlement.id), settlement);
      qc.invalidateQueries({ queryKey: settlementKeys.all });
      qc.invalidateQueries({ queryKey: saleKeys.lists() });
      qc.invalidateQueries({ queryKey: saleKeys.details() });
      qc.invalidateQueries({ queryKey: ["summary"] });
    },
  });
}
